import { normalizeForSearch } from "@/lib/countries";

// Buscador de lugares del formulario de eventos (web y wizard con IA), en
// cualquier país. Usa Photon (OpenStreetMap, gratis, sin key) — a diferencia
// de Nominatim, está pensado para sugerencias mientras se escribe (la
// política de uso de Nominatim prohíbe autocompletar). El servidor igual
// verifica el país con las coordenadas (ver resolvePlace en el backend).

const PHOTON_URL = "https://photon.komoot.io/api/";

// Centro aproximado de cada país del selector, para que las sugerencias
// prefieran lugares de ahí (sin excluir otros países).
const COUNTRY_BIAS: Record<string, [number, number]> = {
  CL: [-33.45, -70.66],
  AR: [-34.6, -58.38],
  MX: [19.43, -99.13],
  US: [40.71, -74.0],
  PE: [-12.05, -77.04],
  UY: [-34.9, -56.19],
  ES: [40.42, -3.7],
  CO: [4.71, -74.07],
};

export interface PlaceSuggestion {
  id: string;
  // Nombre del lugar si es un recinto/punto de interés (teatro, bar, parque).
  name?: string;
  street?: string;
  housenumber?: string;
  // Barrio / comuna / ciudad tal como los entrega OpenStreetMap.
  locality?: string;
  district?: string;
  city?: string;
  state?: string;
  countryCode: string;
  lat: number;
  lng: number;
}

// Lo que el lugar elegido aporta al evento.
export interface EventPlace {
  venue?: string;
  address?: string;
  // Comuna (Chile) o ciudad/barrio (otros países).
  district?: string;
  region?: string;
  country: string;
  location: { lat: number; lng: number };
}

interface PhotonFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    osm_type?: string;
    osm_id?: number;
    type?: string;
    name?: string;
    street?: string;
    housenumber?: string;
    locality?: string;
    district?: string;
    city?: string;
    state?: string;
    countrycode?: string;
  };
}

export async function searchPlaces(query: string, biasCountry?: string, signal?: AbortSignal): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const params = new URLSearchParams({ q, limit: "6" });
  const bias = biasCountry ? COUNTRY_BIAS[biasCountry] : undefined;
  if (bias) {
    params.set("lat", String(bias[0]));
    params.set("lon", String(bias[1]));
  }
  try {
    const res = await fetch(`${PHOTON_URL}?${params}`, { signal });
    if (!res.ok) return [];
    const data = (await res.json()) as { features?: PhotonFeature[] };
    return (data.features ?? [])
      .filter((f) => f.properties.countrycode)
      .map((f) => {
        const p = f.properties;
        // Calles y ciudades no son "recintos": su nombre ya es la dirección
        // o la ciudad.
        const isPlace = p.type === "house" || p.type === "other";
        return {
          id: `${p.osm_type ?? ""}${p.osm_id ?? Math.random()}`,
          name: isPlace && p.name && p.name !== p.street ? p.name : undefined,
          street: p.street ?? (p.type === "street" ? p.name : undefined),
          housenumber: p.housenumber,
          locality: p.locality,
          district: p.district ?? (p.type === "district" ? p.name : undefined),
          city: p.city ?? (p.type === "city" ? p.name : undefined),
          state: p.state,
          countryCode: p.countrycode!.toUpperCase(),
          lat: f.geometry.coordinates[1],
          lng: f.geometry.coordinates[0],
        };
      });
  } catch {
    return [];
  }
}

// Comuna chilena del lugar: OpenStreetMap la entrega a veces como `district`
// (Providencia, Ñuñoa) y a veces como `city` (Santiago). Se cruza con la
// lista de comunas del sitio para no crear variantes que rompan los filtros.
function matchChileanDistrict(s: PlaceSuggestion, districts: string[]): string | undefined {
  const byKey = new Map(districts.map((d) => [normalizeForSearch(d), d]));
  for (const candidate of [s.district, s.city, s.locality]) {
    if (!candidate) continue;
    const hit = byKey.get(normalizeForSearch(candidate));
    if (hit) return hit;
  }
  return s.district ?? s.city;
}

export function toEventPlace(s: PlaceSuggestion, chileanDistricts: string[]): EventPlace {
  const address = [s.street, s.housenumber].filter(Boolean).join(" ") || undefined;
  const district = s.countryCode === "CL" ? matchChileanDistrict(s, chileanDistricts) : s.city ?? s.district ?? s.locality;
  return {
    venue: s.name,
    address,
    district,
    region: s.state,
    country: s.countryCode,
    location: { lat: s.lat, lng: s.lng },
  };
}

// Texto de una sugerencia: "Teatro Solís — Juncal, Montevideo".
export function suggestionLabel(s: PlaceSuggestion): { title: string; detail: string } {
  const street = [s.street, s.housenumber].filter(Boolean).join(" ");
  const city = s.city ?? s.district ?? s.locality;
  const title = s.name ?? (street || city || s.state || s.countryCode);
  const detail = [s.name ? street : undefined, city !== title ? city : undefined, s.state !== city ? s.state : undefined]
    .filter(Boolean)
    .join(", ");
  return { title, detail };
}

// Países con más de una zona horaria: para eventos online se elige cuál.
export const MULTI_TIMEZONE_COUNTRIES: Record<string, { tz: string; city: string }[]> = {
  US: [
    { tz: "America/New_York", city: "New York" },
    { tz: "America/Chicago", city: "Chicago" },
    { tz: "America/Denver", city: "Denver" },
    { tz: "America/Los_Angeles", city: "Los Angeles" },
  ],
  MX: [
    { tz: "America/Mexico_City", city: "Ciudad de México" },
    { tz: "America/Cancun", city: "Cancún" },
    { tz: "America/Tijuana", city: "Tijuana" },
  ],
  BR: [
    { tz: "America/Sao_Paulo", city: "São Paulo" },
    { tz: "America/Manaus", city: "Manaus" },
  ],
  CA: [
    { tz: "America/Toronto", city: "Toronto" },
    { tz: "America/Vancouver", city: "Vancouver" },
  ],
};

// Moneda del precio según el país del evento.
const CURRENCY_BY_COUNTRY: Record<string, string> = {
  CL: "CLP", US: "USD", MX: "MXN", AR: "ARS", PE: "PEN", UY: "UYU", ES: "EUR", CO: "COP",
  BR: "BRL", EC: "USD", BO: "BOB", PY: "PYG", VE: "USD", CR: "CRC", PA: "USD", GT: "GTQ",
  SV: "USD", HN: "HNL", NI: "NIO", DO: "DOP", CU: "CUP", PR: "USD", CA: "CAD", GB: "GBP",
  FR: "EUR", DE: "EUR", IT: "EUR", PT: "EUR",
};

export function currencyFor(country?: string): string {
  return CURRENCY_BY_COUNTRY[country ?? "CL"] ?? "USD";
}
